import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Screen } from '@ghostscreen/contracts';
import { OfflineReadinessLine } from '../../components/dashboard/OfflineReadinessLine';
import { StatusPill } from '../../components/dashboard/StatusPill';
import { SyncStatusPill } from '../../components/dashboard/SyncStatusPill';
import { formatRelativeTime } from '../../lib/dashboard/relativeTime';
import { getCardConnectionStatus, getCardSyncStatus } from '../../lib/dashboard/screenStatus';
import { DisplayPreviewCarousel } from './DisplayPreviewCarousel';
import { useDisplayPreviewSlides } from './useDisplayPreviewSlides';
import { useScreenOfflineReadiness } from './useScreenOfflineReadiness';

type DisplayCardProps = {
  screen: Screen;
  locationName: string;
  onManage: () => void;
  /** Paired: open Manage with the play-content picker expanded. */
  onPlayContent: () => void;
};

function formatVersion(version: number | null, t: TFunction<'displays'>): string {
  return version == null ? t('card.versionUnknown') : `v${version}`;
}

/**
 * Tap card → Manage. Content library opens only from Play content / Add inside Manage.
 */
export function DisplayCard({ screen, locationName, onManage, onPlayContent }: DisplayCardProps) {
  const { t } = useTranslation('displays');
  const connectionStatus = getCardConnectionStatus(screen);
  const syncStatus = getCardSyncStatus(screen);
  const unpaired = connectionStatus === 'unpaired';
  const { slides, isPending } = useDisplayPreviewSlides(screen);
  const { readiness } = useScreenOfflineReadiness(screen, screen.organisationId);

  const lastSeenLabel = unpaired ? t('card.notPaired') : formatRelativeTime(screen.lastHeartbeatAt);
  const versionLabel = unpaired
    ? t('card.notPaired')
    : t('card.versions', {
        desired: formatVersion(screen.desiredPlaylistVersion, t),
        reported: formatVersion(screen.reportedPlaylistVersion, t),
      });

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-line bg-panel transition hover:border-signal/50">
      <button
        type="button"
        onClick={() => {
          if (unpaired) onManage();
          else onPlayContent();
        }}
        className="flex flex-1 flex-col text-start"
        aria-label={
          unpaired ? t('card.pairAria', { name: screen.name }) : t('manage.aria', { name: screen.name })
        }
      >
        <div className="relative aspect-video overflow-hidden border-b border-line bg-panel2">
          {isPending && slides.length === 0 && !unpaired ? (
            <div className="grid h-full place-items-center text-[12px] text-muted">
              {t('preview.loading')}
            </div>
          ) : (
            <DisplayPreviewCarousel slides={slides} fallbackLabel={versionLabel} />
          )}
          <div className="absolute end-2.5 top-2.5 z-10 flex items-center gap-1.5">
            <StatusPill status={connectionStatus} />
            {!unpaired ? <SyncStatusPill status={syncStatus} /> : null}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 px-4 py-3.5">
          <h3 className="text-[16.5px] font-bold tracking-[-0.015em] text-ink">{screen.name}</h3>
          <p className="text-[13px] text-muted">
            {locationName} · {t(`card.orientation.${screen.orientation}`)}
          </p>
          <p className="mt-auto flex items-center gap-1.5 text-[12.5px] text-muted">
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" strokeLinecap="round" />
            </svg>
            {lastSeenLabel}
          </p>
          {readiness ? <OfflineReadinessLine readiness={readiness} density="card" /> : null}
          <span className="mt-1 self-start text-[13px] font-semibold text-signal">
            {unpaired ? t('card.pairAndManage') : t('card.manage')}
          </span>
        </div>
      </button>

      {!unpaired ? (
        <div className="border-t border-line px-4 py-2.5">
          <button
            type="button"
            onClick={onManage}
            className="text-[13px] font-semibold text-muted transition hover:text-ink"
          >
            {t('card.manageSettings')}
          </button>
        </div>
      ) : null}
    </article>
  );
}
